import { Alert, Card, FormGroup, Select, TextInput } from '@ksyos/design-system';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { LanguageOptions } from '@common/enums';
import type { IOption, IOrganization, ISpecialism } from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import { MedicalSpecialistSpecialismCode } from '@common/enums/specialismCode';
import { convertOrganizationTypeToOrganizationTypeWithLinkedProfessions } from '@common/utils/convertOrganizationTypeToOrganizationTypeWithLinkedProfessions';
import { formatString } from '@common/utils/formatString';
import { computeSelectProps } from '@frontend/components/fields/SelectField';
import { getOneOrganization } from '@frontend/services/api/organizationsApiService';
import { getSpecialisms } from '@frontend/services/api/specialismsApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { getSpecialismSelectOptions } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { IIdentifierDeclaration } from '../../../../../common/interfaces/identifierDeclaration';
import type { ValidationErrors } from '../../../../utils/validation';
import type { IEmployment } from '../../../interfaces/employment';
import type { IPractitioner } from '../../../interfaces/practitioner';
import { Profession } from '../../../../../common/enums/profession';
import { employmentGeneralSettingsSchema } from '../../../../../common/validation/employment';
import { registrationCodesFrontendSchema } from '../../../../../common/validation/practitioner';
import { getValidationErrorTranslation } from '../../../../utils/getValidationErrorTranslation';
import {
    isIdentifierDeclarationOrSupervisorRequired,
    isIdentifierDeclarationRequired,
    isIdentifierRegistrationBigAllowed,
    isSupervisorRequired,
} from '../../../../utils/practitionerIdentifiersHelpers';
import { checkDuplicatedRegistrationCodes } from '../../../../utils/registrationCodesValidations';
import { renameErrorKeys } from '../../../../utils/renameErrorKeys';
import { emptyValidationErrors, updateValidationErrorsForOne } from '../../../../utils/validation';
import { SelectProfession } from '../../../elements/SelectProfession';
import { SelectSupervisor } from '../../../elements/SelectSupervisor';
import { IdentifierDeclaration } from '../../../userDataManagement/IdentifierDeclaration';

export interface IPractitionerEmployment {
    employment: IEmployment;
    practitioner: IPractitioner;
}

interface IChooseProfessionProps {
    onChangePractitionerEmployment: (practitionerEmployment: IPractitionerEmployment) => void;
    onValidateOne?: <K extends keyof IPractitioner>(name: K) => Promise<void>;
    originalPractitionerEmployment: IPractitionerEmployment;
    practitionerEmploymentState: IPractitionerEmployment;
    showRegistrationCodesError?: boolean;
}

export function ChooseProfession(props: IChooseProfessionProps) {
    const abortSignal = useAbortSignal();
    const translate = getSelectedLanguage();
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [organization, setOrganization] = useState<IOrganization | undefined>();
    const [specialismOptions, setSpecialismOptions] = useState<IOption<string>[]>([]);
    const [supervisorsAvailable, setSupervisorsAvailable] = useState(true);
    const [selectedSupervisorLabel, setSelectedSupervisorLabel] = useState<string | null>(null);
    const language = useSelector((state: IReduxState) => state.language.code) as LanguageOptions;

    const isMedicalSpecialist =
        props.practitionerEmploymentState.employment.profession === Profession.medicalSpecialist;

    useEffect(() => {
        loadSpecialisms();
    }, []);

    useEffect(() => {
        loadOrganization(props.practitionerEmploymentState.employment.organizationId);
    }, [props.practitionerEmploymentState.employment.organizationId]);

    useEffect(() => {
        if (isMedicalSpecialist === false) {
            handleChangeEmployment('specialismCode', null);
        } else {
            validateOneEmployment('specialismCode', props.practitionerEmploymentState.employment.specialismCode);
        }
    }, [props.practitionerEmploymentState.employment.profession]);

    if (!organization) {
        return null;
    }

    return (
        <Card>
            <Card.Header title={translate.carePath.whichProfessionToAdd} />
            <Card.Body>
                <FormGroup label={translate.common.profession}>
                    <SelectProfession
                        onChange={(profession) => handleChangeEmployment('profession', profession)}
                        organizationType={
                            organization.organizationType
                                ? convertOrganizationTypeToOrganizationTypeWithLinkedProfessions(
                                      organization.organizationType
                                  )
                                : null
                        }
                        placeholder={translate.carePath.selectOrSearchProfession}
                        profession={props.practitionerEmploymentState.employment.profession}
                    />
                </FormGroup>
                {isMedicalSpecialist && (
                    <FormGroup
                        hasErrors={errors.has('specialismCode')}
                        isRequired={true}
                        label={translate.common.specialism}
                    >
                        <Select
                            {...computeSelectProps({
                                error: getValidationErrorTranslation(errors.get('specialismCode')),
                                onChange: (specialismCode) => {
                                    validateOneEmployment('specialismCode', specialismCode);
                                    handleChangeEmployment('specialismCode', specialismCode);
                                },
                                options: specialismOptions,
                                placeholder: translate.common.clickAndChooseFromList,
                                value: props.practitionerEmploymentState.employment.specialismCode,
                            })}
                        />
                    </FormGroup>
                )}
                {renderAlert(
                    props.showRegistrationCodesError && !errors.has('identifierRegistrationBig') ? 'warning' : 'info'
                )}
                {renderIdentifierDeclaration()}
                {renderSupervisor()}
                {renderIdentifierRegistrationBig()}
            </Card.Body>
        </Card>
    );

    async function handleChangeEmployment<K extends keyof IEmployment>(name: K, value: IEmployment[K]) {
        const updatedPractitionerEmployment = cloneDeep(props.practitionerEmploymentState);

        if (name === 'profession') {
            setErrors(emptyValidationErrors);

            const professionsArray = value ? [value as Profession] : [];

            if (isIdentifierDeclarationRequired(professionsArray)) {
                updatedPractitionerEmployment.employment.identifierDeclarationPractitionerId = null;
            } else if (isSupervisorRequired(professionsArray)) {
                updatedPractitionerEmployment.practitioner.identifierDeclaration = null;
            } else if (!isIdentifierDeclarationOrSupervisorRequired(professionsArray)) {
                updatedPractitionerEmployment.employment.identifierDeclarationPractitionerId = null;
                updatedPractitionerEmployment.practitioner.identifierDeclaration = null;
                updatedPractitionerEmployment.practitioner.hasDeclarableQualification = null;
            }

            if (!isIdentifierRegistrationBigAllowed(professionsArray)) {
                updatedPractitionerEmployment.practitioner.identifierRegistrationBig = null;
            }
        }

        updatedPractitionerEmployment.employment[name] = value;

        props.onChangePractitionerEmployment(updatedPractitionerEmployment);
    }

    function renderIdentifierDeclaration() {
        const professionsArray = props.practitionerEmploymentState.employment.profession
            ? [props.practitionerEmploymentState.employment.profession]
            : [];
        if (
            !props.practitionerEmploymentState.employment.profession ||
            props.originalPractitionerEmployment.practitioner.identifierDeclaration ||
            (!isIdentifierDeclarationRequired(professionsArray) &&
                !isIdentifierDeclarationOrSupervisorRequired(professionsArray)) ||
            (isIdentifierDeclarationOrSupervisorRequired(professionsArray) &&
                props.originalPractitionerEmployment.employment.identifierDeclarationPractitionerId)
        ) {
            return null;
        }

        const identifierDeclaration = {
            hasDeclarableQualification: props.practitionerEmploymentState.practitioner.hasDeclarableQualification,
            identifierDeclaration: props.practitionerEmploymentState.practitioner.identifierDeclaration,
        };

        return (
            <IdentifierDeclaration
                errors={errors}
                isRequired={isRequiredForProfession(professionsArray)}
                onChange={async (key, value) => handleChangeIdentifierDeclaration(key, value)}
                onValidateOne={async (name) => await validateRegistrationCodes(name)}
                value={identifierDeclaration}
            />
        );
    }

    async function handleChangeIdentifierDeclaration<K extends keyof IIdentifierDeclaration>(
        name: K,
        value: IIdentifierDeclaration[K]
    ) {
        const updatedPractitionerEmployment = cloneDeep(props.practitionerEmploymentState);
        updatedPractitionerEmployment.practitioner = { ...updatedPractitionerEmployment.practitioner, [name]: value };
        props.onChangePractitionerEmployment(updatedPractitionerEmployment);
    }

    async function validateRegistrationCodes(name: keyof IPractitioner) {
        const practitioner = {
            ...props.practitionerEmploymentState.practitioner,
            professions: [props.practitionerEmploymentState.employment.profession],
        };

        const newErrors = updateValidationErrorsForOne(registrationCodesFrontendSchema, practitioner, errors, name);
        let registrationCodeErrors = await checkDuplicatedRegistrationCodes(practitioner, practitioner.id, [
            'identifierDeclaration',
            'identifierRegistrationBig',
        ]);
        registrationCodeErrors = renameErrorKeys(
            registrationCodeErrors,
            'practitioner.identifierDeclaration',
            'identifierDeclaration'
        );
        registrationCodeErrors = renameErrorKeys(
            registrationCodeErrors,
            'practitioner.identifierRegistrationBig',
            'identifierRegistrationBig'
        );
        setErrors(newErrors.merge(registrationCodeErrors));
    }

    function renderSupervisor() {
        const professionsArray = props.practitionerEmploymentState.employment.profession
            ? [props.practitionerEmploymentState.employment.profession]
            : [];
        if (
            !props.practitionerEmploymentState.employment.profession ||
            props.originalPractitionerEmployment.employment.identifierDeclarationPractitionerId ||
            (!isSupervisorRequired(professionsArray) &&
                !isIdentifierDeclarationOrSupervisorRequired(professionsArray)) ||
            (isIdentifierDeclarationOrSupervisorRequired(professionsArray) &&
                props.originalPractitionerEmployment.practitioner.identifierDeclaration)
        ) {
            return null;
        }

        return (
            <SelectSupervisor
                errors={errors}
                onChangeSupervisor={async (value, label) => {
                    handleChangeEmployment('identifierDeclarationPractitionerId', value);
                    await validateOneEmployment('identifierDeclarationPractitionerId', value || null);
                    setSelectedSupervisorLabel(label || null);
                }}
                onSupervisorsAvailableAtInitialLoad={setSupervisorsAvailable}
                organizationId={props.practitionerEmploymentState.employment.organizationId}
                practitionerId={props.practitionerEmploymentState.employment.practitionerId}
                selectedSupervisor={props.practitionerEmploymentState.employment.identifierDeclarationPractitionerId}
                selectedSupervisorLabel={selectedSupervisorLabel}
            />
        );
    }

    async function validateOneEmployment<K extends keyof IEmployment>(name: K, value: IEmployment[K]) {
        const employmentToValidate = { ...props.practitionerEmploymentState.employment, [name]: value } as IEmployment;

        setErrors(updateValidationErrorsForOne(employmentGeneralSettingsSchema, employmentToValidate, errors, name));
    }

    function renderIdentifierRegistrationBig() {
        if (
            !props.practitionerEmploymentState.employment.profession ||
            props.originalPractitionerEmployment.practitioner.identifierRegistrationBig ||
            !isIdentifierRegistrationBigAllowed([props.practitionerEmploymentState.employment.profession])
        ) {
            return null;
        }

        return (
            <FormGroup
                hasErrors={
                    errors.has('identifierRegistrationBig') || errors.has('practitioner.identifierRegistrationBig')
                }
                label={translate.identifierDeclarationOrRegistration.identifierRegistrationBig}
            >
                <TextInput
                    error={getValidationErrorTranslation(
                        errors.get('identifierRegistrationBig') || errors.get('practitioner.identifierRegistrationBig')
                    )}
                    onBlur={() => validateRegistrationCodes('identifierRegistrationBig')}
                    onChange={(e) =>
                        handleChangePractitioner('identifierRegistrationBig', e.target.value ? e.target.value : null)
                    }
                    placeholder={formatString(translate.personalSettings.addIdentifier, [
                        translate.identifierDeclarationOrRegistration.identifierRegistrationBig,
                    ])}
                    type="text"
                    value={props.practitionerEmploymentState.practitioner.identifierRegistrationBig || ''}
                />
            </FormGroup>
        );
    }

    async function handleChangePractitioner<K extends keyof IPractitioner>(name: K, value: IPractitioner[K]) {
        const updatedPractitionerEmployment = cloneDeep(props.practitionerEmploymentState);
        updatedPractitionerEmployment.practitioner[name] = value;
        props.onChangePractitionerEmployment(updatedPractitionerEmployment);
    }

    function isRequiredForProfession(professionsArray: Profession[]) {
        return (
            isIdentifierDeclarationRequired(professionsArray) ||
            (isIdentifierDeclarationOrSupervisorRequired(professionsArray) &&
                !props.practitionerEmploymentState.employment.identifierDeclarationPractitionerId)
        );
    }

    function renderAlert(appearance: 'info' | 'warning') {
        if (!props.practitionerEmploymentState.employment.profession) {
            return null;
        }

        const professionsArray = [props.practitionerEmploymentState.employment.profession];

        const showIdentifierDeclarationOrSupervisorRequiredAlert =
            !(
                props.originalPractitionerEmployment.practitioner.identifierDeclaration ||
                props.originalPractitionerEmployment.employment.identifierDeclarationPractitionerId
            ) && isIdentifierDeclarationOrSupervisorRequired(professionsArray);

        const showIdentifierDeclarationRequiredAlert =
            !props.originalPractitionerEmployment.practitioner.identifierDeclaration &&
            isIdentifierDeclarationRequired(professionsArray);

        const showSupervisorRequiredAlert =
            !props.originalPractitionerEmployment.employment.identifierDeclarationPractitionerId &&
            isSupervisorRequired(professionsArray);

        let alertMessage: string | null = null;

        if (showIdentifierDeclarationOrSupervisorRequiredAlert) {
            alertMessage = `${translate.identifierDeclarationOrRegistration.identifierDeclarationRequiredBecauseOfProfession} ${
                supervisorsAvailable
                    ? translate.identifierDeclarationOrRegistration.addIdentifierDeclarationOrSupervisorToContinue
                    : translate.identifierDeclarationOrRegistration.addIdentifierDeclarationToContinue
            }`;
        } else if (showIdentifierDeclarationRequiredAlert) {
            alertMessage = `${translate.identifierDeclarationOrRegistration.identifierDeclarationRequiredBecauseOfProfession} ${translate.identifierDeclarationOrRegistration.enterValue}`;
        } else if (showSupervisorRequiredAlert) {
            alertMessage = `${translate.identifierDeclarationOrRegistration.supervisorRequiredBecauseOfProfession} ${translate.identifierDeclarationOrRegistration.enterValue}`;
        }

        if (
            !alertMessage &&
            props.showRegistrationCodesError &&
            props.practitionerEmploymentState.employment.profession === Profession.other
        ) {
            alertMessage = translate.identifierDeclarationOrRegistration.chooseAProfession;
        }

        if (alertMessage) {
            return <Alert appearance={appearance}>{alertMessage}</Alert>;
        }
    }

    async function loadSpecialisms() {
        const result = await getSpecialisms(language, abortSignal);

        const specialisms = result.filter(
            (item: ISpecialism) =>
                item.isActive &&
                Object.values(MedicalSpecialistSpecialismCode).includes(item.code as MedicalSpecialistSpecialismCode)
        );

        const specialismSelectOptions = getSpecialismSelectOptions(specialisms, false);

        setSpecialismOptions(
            specialismSelectOptions.map((specialism) => ({ label: specialism.text, value: specialism.value }))
        );
    }

    async function loadOrganization(organizationId: string) {
        const result = await getOneOrganization(organizationId, abortSignal);
        setOrganization(result);
    }
}
